import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import { ArrowRight, Clock, Users } from "lucide-react";
import { Calendar } from "../../components/ui/calendar";
import api from "../../lib/api";
import { trackEvent } from "../../lib/tracking";
import { useSiteConfig, sel } from "../../lib/site-config";
import HeroBackground from "../../components/vitrine/HeroBackground";

/**
 * Beach Life / Loisirs — Date-first immersive booking page.
 * Same premium pattern as Beach Club: hero image + integrated calendar card,
 * then a smooth-scroll to dynamic activity cards driven by the staff dashboard.
 */

const HERO_IMAGE_FALLBACK =
  "https://images.unsplash.com/photo-1530549387789-4c1017266635?auto=format&fit=crop&w=2400&q=85";

const fmtXOF = (n) => new Intl.NumberFormat("fr-FR").format(n || 0) + " FCFA";

export default function UniversActivites() {
  const [selected, setSelected] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const offersRef = useRef(null);

  const iso = useMemo(() => (selected ? format(selected, "yyyy-MM-dd") : null), [selected]);
  const weekdayLabel = useMemo(
    () => (selected ? format(selected, "EEEE d MMMM yyyy", { locale: frLocale }) : ""),
    [selected]
  );

  // Fetch activities scheduled for the selected date
  useEffect(() => {
    if (!iso) { setActivities([]); return; }
    setLoading(true);
    api
      .get(`/loisirs-activities?date=${iso}`)
      .then((r) => setActivities(Array.isArray(r.data) ? r.data : r.data?.items || []))
      .catch(() => setActivities([]))
      .finally(() => setLoading(false));
  }, [iso]);

  // Smooth scroll to activities section as soon as a date is picked
  useEffect(() => {
    if (!selected || !offersRef.current) return;
    const t = setTimeout(() => {
      offersRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
  }, [selected]);

  return (
    <UniversActivitesInner
      selected={selected}
      setSelected={setSelected}
      activities={activities}
      loading={loading}
      weekdayLabel={weekdayLabel}
      offersRef={offersRef}
      iso={iso}
    />
  );
}

function UniversActivitesInner({ selected, setSelected, activities, loading, weekdayLabel, offersRef, iso }) {
  const cfg = useSiteConfig();
  const ph = sel.pageHeader(cfg, "hero_activites");
  const heroImage = ph.image_url || HERO_IMAGE_FALLBACK;
  const heroKicker = ph.kicker || "· Beach Life ·";
  const heroTitle = ph.title || "Choisissez votre journée";
  const heroSubtitle = ph.subtitle_long || "Sélectionnez la date de votre visite et découvrez les activités proposées ce jour-là. Chaque journée révèle une sélection d'expériences sport et nature, pensées pour vibrer pleinement l'univers BBr.";
  const heroFooter = ph.footer_note || "Jet Ski · Paddle · Padel · Quad · Buggy · Multisports";
  const heroVideo = ph.video_url || "";
  return (
    <div className="bg-white text-[#0A0A0A]" data-testid="univers-activites">
      {/* ─── Immersive Hero (CMS iter-63/64) ─── */}
      <section className="relative w-full min-h-[100vh] overflow-hidden bg-[#0A0A0A]">
        <HeroBackground videoUrl={heroVideo} imageUrl={heroImage} />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/40" />

        <div className="relative z-10 min-h-[100vh] max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-28 flex flex-col items-center justify-center gap-8 md:gap-10">
          {/* MOBILE: title + single-column calendar card */}
          <div className="md:hidden text-center text-white">
            <div className="text-[0.6rem] tracking-[0.55em] uppercase text-white/85 mb-4">
              {heroKicker}
            </div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl leading-[1.05] whitespace-pre-line">
              {heroTitle}
            </h1>
          </div>

          <div
            className="md:hidden w-full max-w-md bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] p-4 sm:p-6"
            data-testid="loisirs-calendar-card-mobile"
          >
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(d) => {
                setSelected(d);
                if (d) trackEvent("loisirs_date_select", { date: format(d, "yyyy-MM-dd") });
              }}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              locale={frLocale}
              className="mx-auto"
              classNames={{
                day_today: "bg-transparent text-[#0A0A0A] hover:bg-[#B8922A]/10",
                day_selected:
                  "bg-[#B8922A] text-white hover:bg-[#B8922A] hover:text-white focus:bg-[#B8922A] focus:text-white",
              }}
              data-testid="loisirs-calendar-mobile"
            />
            {selected && (
              <div className="text-center mt-4 pt-4 border-t border-[#0A0A0A]/10 text-xs tracking-[0.15em] uppercase text-[#0A0A0A]/70">
                <span className="capitalize">{weekdayLabel}</span>
              </div>
            )}
          </div>

          {/* DESKTOP: horizontal landscape card — calendar (left) + context (right) */}
          <div
            className="hidden md:grid w-full max-w-5xl grid-cols-2 bg-white shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)]"
            data-testid="loisirs-calendar-card-desktop"
          >
            {/* LEFT — Calendar */}
            <div className="p-8 lg:p-10 border-r border-[#0A0A0A]/8">
              <Calendar
                mode="single"
                selected={selected}
                onSelect={(d) => {
                  setSelected(d);
                  if (d) trackEvent("loisirs_date_select", { date: format(d, "yyyy-MM-dd") });
                }}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                locale={frLocale}
                className="mx-auto scale-[1.08] origin-top"
                classNames={{
                  day_today: "bg-transparent text-[#0A0A0A] hover:bg-[#B8922A]/10",
                  day_selected:
                    "bg-[#B8922A] text-white hover:bg-[#B8922A] hover:text-white focus:bg-[#B8922A] focus:text-white",
                }}
                data-testid="loisirs-calendar"
              />
              {selected && (
                <div className="text-center mt-6 pt-4 border-t border-[#0A0A0A]/10 text-xs tracking-[0.2em] uppercase text-[#0A0A0A]/70">
                  <span className="capitalize">{weekdayLabel}</span>
                </div>
              )}
            </div>

            {/* RIGHT — Context block */}
            <div className="p-10 lg:p-14 flex flex-col justify-center text-[#0A0A0A]">
              <div className="text-[0.62rem] tracking-[0.55em] uppercase text-[#B8922A] mb-6">
                {heroKicker}
              </div>
              <h1 className="font-serif font-light text-4xl lg:text-5xl leading-[1.05] mb-8 whitespace-pre-line">
                {heroTitle}
              </h1>
              <p className="text-sm lg:text-base leading-[1.85] text-[#0A0A0A]/70 font-light">
                {heroSubtitle}
              </p>
              {heroFooter && (
                <div className="mt-8 pt-6 border-t border-[#0A0A0A]/10 text-[0.6rem] tracking-[0.4em] uppercase text-[#0A0A0A]/45">
                  {heroFooter}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Activities section — dynamic, revealed after date pick ─── */}
      {selected && (
        <section
          ref={offersRef}
          className="py-20 md:py-28 bg-white scroll-mt-24"
          data-testid="loisirs-activities-section"
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-14">
              <div className="text-[0.6rem] tracking-[0.45em] uppercase text-[#B8922A] mb-4">
                · Étape 2 ·
              </div>
              <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight">
                Disponibles ce jour
              </h2>
              <p className="text-sm text-[#0A0A0A]/55 mt-4 capitalize">
                {weekdayLabel}
              </p>
            </div>

            {loading ? (
              <div className="text-center text-sm text-[#0A0A0A]/45">Chargement des activités…</div>
            ) : activities.length === 0 ? (
              <div className="max-w-lg mx-auto text-center py-16">
                <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#0A0A0A]/45 mb-3">
                  · Rien de programmé ·
                </div>
                <p className="text-base text-[#0A0A0A]/65 font-light">
                  Aucune activité n&apos;est programmée à cette date. Choisissez un autre jour
                  ou contactez notre conciergerie pour organiser votre expérience sur-mesure.
                </p>
                <Link
                  to="/contact"
                  className="inline-block mt-6 text-[0.7rem] tracking-[0.3em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A] pb-1 hover:text-[#B8922A] hover:border-[#B8922A] transition-colors"
                >
                  Nous contacter
                </Link>
              </div>
            ) : (
              <div className="grid gap-8 md:grid-cols-2" data-testid="loisirs-activity-grid">
                {activities.map((a) => (
                  <ActivityCard key={a.id} activity={a} iso={iso} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function ActivityCard({ activity, iso }) {
  const {
    id, name_fr, description_fr, image_url,
    price_adult, duration_min, capacity, category,
  } = activity;

  const bookingUrl = `/booking/offres_loisirs?date=${iso}&activity=${encodeURIComponent(id)}`;

  return (
    <article
      className="group flex flex-col bg-white border border-[#0A0A0A]/8 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.15)] hover:shadow-[0_30px_70px_-20px_rgba(0,0,0,0.25)] transition-shadow duration-500 overflow-hidden"
      data-testid={`activity-card-${id.slice(0, 8)}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#F4F0E9]">
        {image_url ? (
          <img
            src={image_url}
            alt={name_fr}
            className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.05]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#0A0A0A]/25 text-xs tracking-widest uppercase">
            Photo à venir
          </div>
        )}
        {category && (
          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 text-[0.55rem] tracking-[0.35em] uppercase text-[#0A0A0A]">
            {category}
          </div>
        )}
      </div>

      <div className="p-7 md:p-8 flex flex-col flex-1">
        <h3 className="font-serif font-light text-2xl md:text-3xl leading-tight text-[#0A0A0A] mb-3">
          {name_fr}
        </h3>
        {description_fr && (
          <p className="text-sm text-[#0A0A0A]/65 font-light leading-relaxed mb-5 line-clamp-3">
            {description_fr}
          </p>
        )}

        <div className="flex items-center gap-5 text-[0.7rem] tracking-[0.15em] uppercase text-[#0A0A0A]/55 mb-6">
          {duration_min > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Clock size={12} strokeWidth={1.5} /> {duration_min} min
            </span>
          )}
          {capacity > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Users size={12} strokeWidth={1.5} /> {capacity} pers.
            </span>
          )}
        </div>

        <div className="mt-auto flex items-end justify-between gap-4 pt-4 border-t border-[#0A0A0A]/10">
          <div>
            <div className="text-[0.55rem] tracking-[0.4em] uppercase text-[#0A0A0A]/45">À partir de</div>
            <div className="font-serif text-2xl text-[#0A0A0A] mt-0.5">{fmtXOF(price_adult)}</div>
          </div>
          <Link
            to={bookingUrl}
            onClick={() => trackEvent("loisirs_activity_select", { id, date: iso })}
            className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#B8922A] text-white px-6 py-3 text-[0.68rem] tracking-[0.32em] uppercase transition-colors duration-300"
            data-testid={`activity-book-${id.slice(0, 8)}`}
          >
            Réserver
            <ArrowRight size={12} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </article>
  );
}
